import { Button, Classes, Icon, Intent, Position, ProgressBar, Tag } from '@blueprintjs/core';
import { Tooltip2 } from '@blueprintjs/popover2';
import { Button as M1Button, Pagination, css, styled } from '@moleculeone/ui';
import { Elements, ExportedSearchResultInList } from '@retro-ui/api-contract';
import { SearchResult } from '@retro-ui/prisma';
import { keepPreviousData } from '@tanstack/react-query';
import cx from 'classnames';
import { chunk, isArray, mapValues } from 'lodash';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useHistory, useLocation, useRouteMatch } from 'react-router-dom';

import { apiClient, apiQueryClient } from '@f/api-client';
import { AppToaster } from '@f/components/notification-toaster/AppToaster';
import { getReportPdf } from '@f/components/pages/dashboard/reports/utils/getReportPdf';
import BatchSearchStatusTag from '@f/components/shared/batch-search-status-tag/BatchSearchStatusTag';
import RemovalTooltip from '@f/components/shared/removal-tooltip/RemovalTooltip';
import { SearchDetails } from '@f/components/shared/search-details/SearchDetails';
import Table from '@f/components/shared/table/Table';
import Toolbar from '@f/components/shared/toolbar/Toolbar';
import { SciFinderAuthContext } from '@f/context';
import { useConfig, useInterval } from '@f/hooks';
import { useIsDebugModeOn } from '@f/hooks/useIsDebugModeOn';
import { useThrottle } from '@f/hooks/useThrottle';
import {
  addToReport,
  getRequest,
  removeRequests,
} from '@f/services/batch-search/batch-search.actions';
import {
  getReportItemsNumber,
  removeReportItems,
  removeReports,
} from '@f/services/report/report.actions';
import { sessionStorageClient } from '@f/services/storage/wrapper';
import { saveAsCSV } from '@f/utils/export';

import { BatchSearchRequestName } from '../../../components/BatchSearchRequestName';
import ReportTreePopover from '../components/ReportTreePopover';
import SearchInfo from '../components/SearchInfo';
import { SearchResultListPageRow } from '../components/SearchResultListPageRow';
import {
  ResultFilters,
  SearchResultFilters,
  getInitialFilters,
} from '../components/filtering/SearchResultFilters';
import { buildResultQuery } from '../util/buildResultQuery';
import { deserializeFilters } from '../util/deserializeFilters';
import { serializeFilters } from '../util/serializeFilters';

import cls from './SearchResultListPage.module.css';

const ToastSpan = styled('span', { display: 'block', marginBottom: '0.3em' });
const ToastButtonsContainer = styled('div', {
  display: 'flex',
  gap: '0.5rem',
  marginTop: '0.5rem',
});

const DebugButton = styled(M1Button, {
  fontFamily: '$mono',
});

const exportButton = css({
  minWidth: '7rem',
});

type SelectedResult = Pick<SearchResult, 'id' | 'reactionCount'>;

const storeFilters = (searchId: string, filters: ResultFilters) => {
  sessionStorageClient.setItem(`filters-${searchId}`, JSON.stringify(filters));
};
const storeSelectedResults = (searchId: string, results: SelectedResult[]) => {
  sessionStorageClient.setItem(`selected_results-${searchId}`, JSON.stringify(results));
};

const REFRESH_DATA_INTERVAL = 9000;
const ADD_TO_REPORT_TOAST_KEY = 'add_to_report_toast';
const REPORT_ADD_CHUNK_SIZE = 10;
const RESULT_EXPORT_CHUNK_SIZE = 10;

export function parseFilters(filtersFromStorage: string) {
  const filters = JSON.parse(filtersFromStorage);
  return {
    ...filters,
    price: filters.price?.map((x: number | null) => {
      if (x == null) {
        return undefined;
      }
      return x;
    }),
  };
}

export function SearchResultListPage() {
  const config = useConfig();
  const isDebugModeOn = useIsDebugModeOn();

  const sciFinderAuth = useContext(SciFinderAuthContext);
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const match = useRouteMatch<{
    batchSearchId: string;
  }>();
  const { batchSearchId } = match.params;

  const [page, setPage] = useState(() => {
    const urlSearchParams = new URLSearchParams(location.search);
    const page = urlSearchParams.get('page');
    if (page) {
      return Number(page);
    }
    return 1;
  });

  const { search, isAddingToReport, isGetting } = useSelector((state: any) => {
    const requestState = state.batchSearch.request;
    const search = requestState.data[batchSearchId];
    const isGetting = requestState.get.processing > 0;
    const resultState = state.batchSearch.result;

    return {
      search,
      isAddingToReport: resultState.addToReport.processing > 0,
      isGetting,
    };
  });

  const [isExporting, setIsExporting] = useState(false);
  const [exportingProgress, setExportingProgress] = useState(0);

  const shouldRefreshRequest =
    (search?.status?.queued > 0 || search?.status?.running > 0) && !search.isRemoved;

  const fetchSearch = () => {
    dispatch(getRequest(search.id));
  };

  const { countdown, reset: resetCountdown } = useInterval(
    () => {
      if (shouldRefreshRequest) {
        fetchSearch();
      }
    },
    shouldRefreshRequest ? REFRESH_DATA_INTERVAL : null
  );

  const [filters, setFilters] = useState(() => {
    const initialFilters = getInitialFilters();

    if (location.search) {
      const urlSearchParams = new URLSearchParams(location.search);
      const filtersFromUrl = deserializeFilters(urlSearchParams);
      const completeFilters = { ...initialFilters, ...filtersFromUrl };
      storeFilters(batchSearchId, completeFilters);
      return completeFilters;
    }

    const filtersFromStorage = sessionStorageClient.getItem(`filters-${batchSearchId}`);
    if (filtersFromStorage) {
      const parsedFilters = parseFilters(filtersFromStorage);
      return parsedFilters;
    }

    return initialFilters;
  });

  useEffect(() => {
    if (sciFinderAuth.isAuthenticating) {
      return;
    }
    const serializedFilters = serializeFilters(filters);
    const filtersWithValue = Object.entries(serializedFilters).filter(([, value]) => value !== '');
    const searchParams = new URLSearchParams(filtersWithValue);
    searchParams.set('page', String(page));
    const newSearchParamsString = searchParams.toString();

    history.replace({
      search: newSearchParamsString,
    });
  }, [filters, page, sciFinderAuth.isAuthenticating]);

  const [selectedResults, setSelectedResults] = useState<SelectedResult[]>(() => {
    const resultsFromStorage = sessionStorageClient.getItem(`selected_results-${batchSearchId}`);
    if (resultsFromStorage) {
      const parsedResults = JSON.parse(resultsFromStorage);
      return parsedResults;
    }
    return [];
  });
  useEffect(() => {
    storeSelectedResults(batchSearchId, selectedResults);
  }, [selectedResults]);

  const resultQueryParams = useMemo(() => buildResultQuery(filters, page), [filters, page]);
  const resultsQueryKey = useMemo(
    () => ['results', batchSearchId, resultQueryParams, search.status],
    [batchSearchId, resultQueryParams, search.status]
  );
  const throttledQueryKey = useThrottle(resultsQueryKey);
  const resultsQuery = apiQueryClient.search.fetchSearchResults.useQuery(
    throttledQueryKey,
    {
      params: {
        searchId: search.id,
      },
      query: resultQueryParams,
    },
    {
      placeholderData: keepPreviousData,
      queryKey: throttledQueryKey,
      staleTime: search.status.finished === search.size ? Infinity : 0,
    }
  );

  const resultIdsQueryKey = useMemo(
    () => ['resultIds', batchSearchId, resultQueryParams, search.status],
    [batchSearchId, resultQueryParams, search.status]
  );
  const throttledResultIdsQueryKey = useThrottle(resultIdsQueryKey);
  const allResultsQuery = apiQueryClient.search.fetchAllSearchResults.useQuery(
    throttledResultIdsQueryKey,
    {
      params: {
        searchId: search.id,
      },
      query: resultQueryParams,
    },
    {
      placeholderData: keepPreviousData,
      queryKey: throttledResultIdsQueryKey,
      staleTime: search.status.finished === search.size ? Infinity : 0,
    }
  );

  const results = resultsQuery.data?.body.data;
  const pagination = resultsQuery.data?.body.pagination;
  const allResults = allResultsQuery.data?.body ?? [];

  const changeFilters = (filters: ResultFilters) => {
    storeFilters(batchSearchId, filters);
    setPage(1);
    setSelectedResults([]);
    setFilters(filters);
  };

  async function addToReportWithRetry(
    params: {
      reportId: string;
      elements: Elements;
      searchResultIds: string[];
      searchId: string;
      scifinderToken: string;
      includeScifinderRelatedData: boolean;
    },
    retries = 2
  ) {
    let attempt = 0;
    while (attempt <= retries) {
      try {
        const ids = await dispatch(addToReport(params));
        return ids;
      } catch (error) {
        console.error(`Action failed after ${attempt} retries`, error);
        attempt++;
        if (attempt > retries) {
          throw error;
        }
      }
    }
  }

  const addSynthesisToReport = async (
    reportId: string,
    elements: Elements,
    includeScifinderRelatedData: boolean
  ) => {
    const { accessToken } = sciFinderAuth;

    const createdReportItemIds: string[] = [];

    const resultWithSynthesisIds = selectedResults
      .filter(result => result.reactionCount != null)
      .map(result => result.id);

    const chunks = chunk(resultWithSynthesisIds, REPORT_ADD_CHUNK_SIZE);
    const chunkCount = chunks.length;
    let currentChunk = 0;

    for (const chunk of chunks) {
      AppToaster.show(
        {
          message: (
            <>
              <ToastSpan>Generating report elements...</ToastSpan>
              <ProgressBar
                value={Math.max(currentChunk / chunkCount, 0.05)}
                intent={Intent.PRIMARY}
              />
            </>
          ),
          timeout: 0,
          isCloseButtonShown: false,
        },
        ADD_TO_REPORT_TOAST_KEY
      );

      try {
        const ids = await addToReportWithRetry({
          reportId,
          elements,
          searchResultIds: chunk,
          searchId: search.id,
          scifinderToken: String(accessToken),
          includeScifinderRelatedData,
        });

        if (Array.isArray(ids)) {
          createdReportItemIds.push(...ids);
        }
      } catch (error) {
        AppToaster.show(
          {
            intent: Intent.DANGER,
            icon: 'error',
            message:
              'An error occurred while adding the selected results to the report. Please try again later.',
          },
          ADD_TO_REPORT_TOAST_KEY
        );

        await dispatch(removeReportItems(reportId, createdReportItemIds));

        const reportItemsNumber = await dispatch(getReportItemsNumber(reportId));
        const emptyReport = Number(reportItemsNumber) === 0;

        if (emptyReport) {
          await dispatch(removeReports([reportId]));
        }

        return;
      }

      currentChunk++;
    }

    AppToaster.show(
      {
        intent: Intent.SUCCESS,
        icon: 'tick',
        message: (
          <>
            <ToastSpan>Report is ready to view</ToastSpan>
            <ToastButtonsContainer>
              <Button
                minimal
                text="Show report"
                icon="document-open"
                onClick={() => {
                  history.push(`/dashboard/reports/${reportId}`);
                }}
                data-cy="button-toast-report-show"
              />
              <Button
                minimal
                text="Download PDF"
                icon="download"
                onClick={() => {
                  getReportPdf(reportId);
                }}
                data-cy="button-toast-report-download"
              />
            </ToastButtonsContainer>
          </>
        ),
      },
      ADD_TO_REPORT_TOAST_KEY
    );
  };

  const navigateToItem = (item, _, event) => {
    if (window.getSelection()?.type === 'Range') {
      return;
    }
    if (event.target.matches('a')) {
      return;
    }

    history.push(`/dashboard/batch/${search.id}/result/${item.id}`);
  };

  const exportResults = async () => {
    setIsExporting(true);
    setExportingProgress(0);

    const resultIds = allResults.map(result => result.id);

    const chunks = chunk(resultIds, RESULT_EXPORT_CHUNK_SIZE);
    const chunkCount = chunks.length;
    let currentChunk = 0;
    let exportedResults: ExportedSearchResultInList[] = [];

    for (const chunk of chunks) {
      setExportingProgress(Math.max(currentChunk / chunkCount, 0.05));

      const { status, body } = await apiClient.search.exportSearchResults({
        params: {
          searchId: search.id,
        },
        query: {
          where: {
            id: { in: chunk },
          },
        },
      });
      if (status !== 200) {
        throw new Error('Failed to fetch search results.');
      }
      exportedResults = exportedResults.concat(body.data);
      currentChunk++;
    }

    setIsExporting(false);
    setExportingProgress(1);

    const dataToExport = exportedResults.map(item => {
      const preparedItem = mapValues(item, value =>
        typeof value === 'object' ? JSON.stringify(value) : value
      );
      return preparedItem;
    });

    if (dataToExport.length === 0) {
      return;
    }

    saveAsCSV(dataToExport, `search-results-${search.id}`, {
      columnHeaders: [
        'id',
        'name',
        'targetSmiles',
        'targetMolfile',
        'batchId',
        'status',
        'result',
        'reactionCount',
        'referenceCount',
        'certainty',
        'price',
        'synthesis',
        'searchGraph',
        'decomposition',
        'timedOut',
        'startedAt',
        'finishedAt',
        'sciFinderId',
        'order',
        'name',
        'runningTime',
        'targetMetadata',
      ],
    });
  };

  const rerunSearch = () => {
    history.push(`/dashboard/batch/new?rerun=${search.id}`);
  };

  const removeSearch = async () => {
    await dispatch(removeRequests([search.id]));
    history.push('/dashboard/batch');
  };

  const { isRemoved, status } = search;
  const isRequestFinished = status.queued === 0 && status.running === 0;
  const showPagination = pagination && results.length > 0;
  const hasReferenceCount = isArray(results) && results[0]?.referenceCount != null;

  return (
    <>
      <Toolbar className={cls.toolbar}>
        <Toolbar.Item>
          <NavLink className={cx(Classes.BUTTON, Classes.MINIMAL)} to="/dashboard/batch">
            <Icon icon="double-chevron-left" />
            <span className={Classes.BUTTON_TEXT}>Back to searches</span>
          </NavLink>
        </Toolbar.Item>

        <Toolbar.Item>
          <BatchSearchStatusTag
            large
            status={status}
            removed={isRemoved}
            data-cy="text-search-status"
          />
        </Toolbar.Item>

        <Toolbar.Item>
          <BatchSearchRequestName batch={search} copyable showButton />
        </Toolbar.Item>

        <Toolbar.Item>
          <SearchDetails search={search} />
        </Toolbar.Item>

        {isDebugModeOn && (
          <Toolbar.Item>
            <DebugButton
              text="View hypertrees"
              size="small"
              weight="minimal"
              theme="purple"
              icon="open_in_new"
              onClick={() => {
                window.open(
                  `${config.hypertreeUrl}/search/${search.id}?importFrom=${window.location.origin}`,
                  '_blank'
                );
              }}
            />
          </Toolbar.Item>
        )}

        <Toolbar.Item alignRight>
          {search.shareInfo.sharedWithMe && (
            <Tooltip2 content={`by ${search.shareInfo.ownerEmail}`} placement={Position.BOTTOM}>
              <Tag large intent={Intent.PRIMARY}>
                Shared
              </Tag>
            </Tooltip2>
          )}
        </Toolbar.Item>

        {!isRemoved && (
          <Toolbar.Item alignRight className={exportButton()}>
            {isExporting ? (
              <ProgressBar value={exportingProgress} intent={Intent.PRIMARY} />
            ) : (
              <Button
                minimal
                disabled={isGetting || status.finished === 0}
                text={`Export ${!isRequestFinished ? 'partial' : ''} results`}
                icon="export"
                onClick={exportResults}
                data-cy="button-search-results-export"
              />
            )}
          </Toolbar.Item>
        )}

        {shouldRefreshRequest && !isRemoved && (
          <Toolbar.Item alignRight>
            <Button
              minimal
              loading={isGetting || countdown === 0}
              text={`Refresh status (${countdown})`}
              icon="refresh"
              onClick={() => {
                fetchSearch();
                resetCountdown();
              }}
              data-cy="search-refresh-status"
            />
          </Toolbar.Item>
        )}

        {isRequestFinished && (
          <Toolbar.Item alignRight>
            <Button minimal text="Re-run" icon="redo" onClick={rerunSearch} />
          </Toolbar.Item>
        )}

        <Toolbar.Item alignRight>
          {!search.shareInfo.sharedWithMe && !isRemoved && (
            <RemovalTooltip
              renderTarget={props => (
                <Button
                  minimal
                  intent={Intent.DANGER}
                  text="Remove search"
                  icon="trash"
                  onClick={props.onClick}
                />
              )}
              onRemove={removeSearch}
            />
          )}
        </Toolbar.Item>
      </Toolbar>

      <div className={cls.info}>
        <SearchInfo search={search} />
      </div>

      <div className={cls.container}>
        <div className={cls.list}>
          <div className={cls.listHeader}>
            <div className={cls.listHeaderLeft}>
              <ReportTreePopover
                canCreateReport
                loading={isAddingToReport}
                onAddToReport={addSynthesisToReport}
                selectedResults={selectedResults}
                searchName={search.name}
                shouldEnableEditingMode={false}
              />
              {showPagination && (
                <Tag large minimal round>
                  {selectedResults.length} selected / {pagination.total} matching results
                </Tag>
              )}
            </div>

            {showPagination && (
              <Pagination
                currentPage={page}
                itemCount={pagination.total}
                pageSize={filters.pageSize}
                onChange={setPage}
              />
            )}
          </div>

          <Table
            className={cx(cls.table, { [cls.isFetchingResults]: resultsQuery.isFetching })}
            schema={SearchResultListPageRow(search, hasReferenceCount)}
            items={results}
            interactive
            loading={resultsQuery.isPending}
            loadingCaption="Loading results..."
            tableProps={{
              className: cls.table,
            }}
            headingRowProps={{
              className: cls.headingRow,
            }}
            onRowClick={navigateToItem}
            selectable
            isRowSelected={item => selectedResults.find(result => result.id === item.id)}
            onRowSelectionChange={item => {
              setSelectedResults(selectedResults => {
                const existingResult = selectedResults.find(result => result.id === item.id);
                if (existingResult) {
                  return selectedResults.filter(result => result.id !== item.id);
                }
                const { id, reactionCount } = item;
                const sortedResults = [...selectedResults, { id, reactionCount }].sort((a, b) => {
                  const aIndex = allResults.findIndex(result => result.id === a.id);
                  const bIndex = allResults.findIndex(result => result.id === b.id);
                  return aIndex - bIndex;
                });
                return sortedResults;
              });
            }}
            onAllRowsSelectionChange={() => {
              const allSelected = pagination?.total === selectedResults.length;
              setSelectedResults(allSelected ? [] : allResults);
            }}
            data-cy="table-search-results"
          />

          {showPagination && (
            <Pagination
              currentPage={page}
              itemCount={pagination.total}
              pageSize={filters.pageSize}
              onChange={setPage}
            />
          )}
        </div>

        {filters && (
          <SearchResultFilters
            className={cls.filters}
            resultCount={pagination?.total ?? 0}
            statistics={search.statistics}
            filters={filters}
            onFiltersChange={changeFilters}
            hasReferenceCount={hasReferenceCount}
          />
        )}
      </div>
    </>
  );
}
